import { makeStyles } from '@material-ui/styles'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import React, { useCallback, useEffect, useState } from 'react'
import { cloudProviderActions } from 'app/plugins/infrastructure/components/cloudProviders/actions'
import Theme from 'core/themes/model'
import Card from './card'
import useDataUpdater from 'core/hooks/useDataUpdater'
import AddCloudProviderCredentialStep from 'app/plugins/infrastructure/components/cloudProviders/AddCloudProviderCredentialStep'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import PrevButton from 'core/components/buttons/PrevButton'
import { onboardClusterTracking } from './tracking'
import NextButton from 'core/components/buttons/NextButton'

const useStyles = makeStyles<Theme>((theme) => ({
  cloudProviders: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  form: {
    minHeight: '500px',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  header: {
    marginTop: '0px',
  },
}))

const AddCloudProviderPage = ({
  cloudProviders,
  wizardContext,
  setWizardContext,
  onNext,
  handleNext,
  setSubmitting,
  clusterChoice,
  handleBack,
}) => {
  const classes = useStyles()
  const [activeCloudProvider, setActiveCloudProvider] = useState(null)
  const [deleteCloudProvider] = useDataUpdater(cloudProviderActions.delete)
  const [showExistingCloudProviders, setShowExistingCloudProviders] = useState(
    cloudProviders.length !== 0,
  )
  const [error, setError] = useState(null)

  const handleOnClick = (cloudProvider) => () => {
    setActiveCloudProvider(cloudProvider)
    setWizardContext({ cloudProviderId: cloudProvider.uuid, provider: cloudProvider.type })
  }

  const handleDelete = (cloudProvider) => async () => {
    setSubmitting(true)
    await deleteCloudProvider(cloudProvider)
    setSubmitting(false)
  }

  const toggleForms = () => {
    setActiveCloudProvider({ cloudProviderId: null })
    setShowExistingCloudProviders(false)
  }

  const submitStep = useCallback(() => {
    if (!wizardContext.cloudProviderId) {
      setError('Must select an existing cloud provider or create a new one')
      return false
    }
    onboardClusterTracking.wZSelectedCloudProvider(
      activeCloudProvider.name,
      activeCloudProvider.type,
    )
    setError(null)
    return true
  }, [wizardContext])

  useEffect(() => {
    if (showExistingCloudProviders) {
      onNext(submitStep)
    }
  }, [submitStep, showExistingCloudProviders])

  return (
    <>
      {!showExistingCloudProviders && (
        <AddCloudProviderCredentialStep
          wizardContext={wizardContext}
          setWizardContext={setWizardContext}
          onNext={onNext}
          handleNext={handleNext}
          setSubmitting={setSubmitting}
          header={
            clusterChoice === 'import' ? 'Select the Cloud to Import Clusters From' : undefined
          }
          headerClass={classes.header}
        />
      )}
      {showExistingCloudProviders && (
        <>
          <FormFieldCard
            className={classes.form}
            title="Select an existing cloud provider or create a new one"
          >
            <div className={classes.cloudProviders}>
              {cloudProviders?.map((cp) => (
                <Card
                  key={cp.name}
                  label={cp.name}
                  active={activeCloudProvider?.name === cp.name}
                  onClick={handleOnClick(cp)}
                  onDelete={handleDelete(cp)}
                />
              ))}
              <Card
                label="+ Add a new cloud provider"
                showTrashIcon={false}
                onClick={toggleForms}
              />
            </div>
          </FormFieldCard>
          {error && (
            <div className={classes.errorMessage}>
              <ErrorMessage>{error}</ErrorMessage>
            </div>
          )}
        </>
      )}
      <PrevButton onClick={handleBack} />
      <NextButton onClick={handleNext} showForward={false}>
        {showExistingCloudProviders ? '+ Select Cloud Provider' : `+ Save Cloud Provider`}
      </NextButton>
    </>
  )
}

export default AddCloudProviderPage
